import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { chatActions } from 'store';
import { MessageBubble } from './messageBubble';
import { MESSAGE_PAGE_LIMIT, MESSAGE_TYPE, groupMessagesByDate, formatDateSeparator } from './constants';

export const MessageList = ({ chatId, currentUserId, currentUserType, onDeleteMessage }) => {
    const dispatch = useDispatch();
    const messages = useSelector((state) => state.chat.messages);
    const messageCount = useSelector((state) => state.chat.messageCount);
    const activeChat = useSelector((state) => state.chat.activeChat);
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const containerRef = useRef(null);
    const prevMessagesLenRef = useRef(0);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const loadMoreCallbackRef = useRef(null);

    const hasMore = messages.length < messageCount;

    // Store loadMore in a ref to avoid recreating the IntersectionObserver
    loadMoreCallbackRef.current = () => {
        if (messages.length >= messageCount) return;
        dispatch(
            chatActions.getMessages(
                chatId,
                {
                    skip: messages.length,
                    limit: MESSAGE_PAGE_LIMIT,
                },
                'noLoad'
            )
        );
    };

    // IntersectionObserver for infinite scroll - only recreate when hasMore changes
    useEffect(() => {
        if (!loadMoreRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreCallbackRef.current?.();
                }
            },
            { threshold: 0.1 }
        );

        observerRef.current.observe(loadMoreRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messages.length > prevMessagesLenRef.current && prevMessagesLenRef.current > 0) {
            if (containerRef.current) {
                const { scrollTop } = containerRef.current;
                const isNearBottom = Math.abs(scrollTop) < 150;
                if (isNearBottom) {
                    containerRef.current.scrollTop = 0;
                }
            }
        } else if (prevMessagesLenRef.current === 0 && messages.length > 0) {
            if (containerRef.current) {
                containerRef.current.scrollTop = 0;
            }
        }
        prevMessagesLenRef.current = messages.length;
    }, [messages.length]);

    const handleScroll = useCallback(() => {
        if (!containerRef.current) return;
        const { scrollTop } = containerRef.current;
        setShowScrollBtn(Math.abs(scrollTop) > 300);
    }, []);

    const scrollToBottom = useCallback(() => {
        if (containerRef.current) {
            containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    const grouped = groupMessagesByDate(messages);
    const dateKeys = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));

    if (!messages.length) {
        return (
            <div className="chat-message-list-container">
                <div className="chat-empty-state">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <rect width="48" height="48" rx="24" fill="#F2F4F7" />
                        <path d="M16 20h16M16 24h10" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" />
                        <rect x="12" y="14" width="24" height="20" rx="4" stroke="#98A2B3" strokeWidth="1.5" />
                    </svg>
                    <p>No messages yet. Start the conversation!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="chat-message-list-container" ref={containerRef} onScroll={handleScroll} role="log" aria-label="Message history">
            {dateKeys.map((dateKey) => (
                <div key={dateKey}>
                    <div className="chat-date-separator">
                        <span>{formatDateSeparator(dateKey)}</span>
                    </div>
                    {[...grouped[dateKey]].reverse().map((msg) =>
                        msg.type === MESSAGE_TYPE.SYSTEM ? (
                            <div key={msg.id} className="chat-system-message">
                                <span>{msg.text}</span>
                            </div>
                        ) : (
                            <MessageBubble
                                key={msg.id}
                                message={msg}
                                isSent={msg.sender?.userId === currentUserId}
                                isGroup={activeChat?.isGroup}
                                onDelete={onDeleteMessage}
                                currentUserId={currentUserId}
                                currentUserType={currentUserType}
                            />
                        )
                    )}
                </div>
            ))}
            {hasMore && <div ref={loadMoreRef} className="chat-load-more-trigger" />}

            {showScrollBtn && (
                <button
                    className="chat-scroll-to-bottom-btn"
                    onClick={scrollToBottom}
                    title="Scroll to latest"
                    aria-label="Scroll to latest messages"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                            d="M8 3v10m0 0l-4-4m4 4l4-4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            )}
        </div>
    );
};
